import React, { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import { api } from "../../lib/api";
import { Card } from "../../components/UI";
import {
  ListChecks, ArrowRight, Loader2, AlertTriangle, Info, Zap,
  CalendarDays, HeartHandshake, Users, ShieldCheck,
} from "lucide-react";

const PRIORITY_STYLE = {
  critical: { icon: AlertTriangle, cls: "text-rose-300 bg-rose-500/10 border-rose-500/30", label: "Critical" },
  important: { icon: Zap, cls: "text-amber-300 bg-amber-400/10 border-amber-400/30", label: "Important" },
  info: { icon: Info, cls: "text-cyan-300 bg-cyan-400/10 border-cyan-400/30", label: "FYI" },
};

const QUICK_LINKS = [
  { icon: CalendarDays, label: "Events", to: "/events", testid: "ops-link-events" },
  { icon: HeartHandshake, label: "Volunteers", to: "/volunteers", testid: "ops-link-volunteers" },
  { icon: Users, label: "Team", to: "/team", testid: "ops-link-team" },
  { icon: CalendarDays, label: "Campaign Calendar", to: "/calendar", testid: "ops-link-calendar" },
  { icon: ShieldCheck, label: "Compliance Center", to: "/compliance", testid: "ops-link-compliance" },
];

export default function SimpleOperations() {
  const navigate = useNavigate();
  const [items, setItems] = useState(null);

  useEffect(() => {
    api.get("/political-os/attention")
      .then((r) => setItems(r.data?.items || []))
      .catch(() => setItems([]));
  }, []);

  if (items === null) return <div className="p-12 flex justify-center"><Loader2 className="w-6 h-6 animate-spin text-cyan-300" /></div>;

  return (
    <div className="p-6 lg:p-10 max-w-4xl mx-auto" data-testid="simple-operations">
      <div className="text-[11px] uppercase tracking-widest text-cyan-300 font-semibold mb-2">Operations</div>
      <h1 className="font-display text-3xl sm:text-4xl font-bold mb-2">Your work queue</h1>
      <p className="text-sm text-slate-400 mb-6 max-w-2xl">
        Approvals, compliance, tasks and deadlines — everything that needs a human decision, in one place.
      </p>

      <Card className="mb-6" padded={false} data-testid="ops-queue">
        <div className="px-5 py-4 border-b border-white/5 flex items-center gap-2">
          <ListChecks className="w-4 h-4 text-cyan-300" />
          <h3 className="font-display text-lg font-bold">Needs your attention</h3>
        </div>
        {items.length === 0 ? (
          <p className="text-sm text-slate-500 px-5 py-6">All clear — nothing waiting on you.</p>
        ) : (
          <div className="divide-y divide-white/5">
            {items.map((i, idx) => {
              const s = PRIORITY_STYLE[i.priority] || PRIORITY_STYLE.info;
              return (
                <button key={idx} onClick={() => navigate(i.link || "/operations/advanced")} data-testid={`ops-item-${idx}`}
                  className="w-full text-left px-5 py-4 flex items-center gap-4 hover:bg-white/[0.03] transition-colors">
                  <span className={`w-8 h-8 rounded-lg border flex items-center justify-center shrink-0 ${s.cls}`}>
                    <s.icon className="w-4 h-4" />
                  </span>
                  <span className="min-w-0 flex-1">
                    <span className="block text-sm text-white">{i.title}</span>
                    <span className="block text-[11px] text-slate-500">{i.detail}</span>
                  </span>
                  <span className={`text-[9px] uppercase tracking-wider px-2 py-0.5 rounded-full border shrink-0 ${s.cls}`}>{s.label}</span>
                  <ArrowRight className="w-4 h-4 text-slate-600 shrink-0" />
                </button>
              );
            })}
          </div>
        )}
      </Card>

      <div className="flex flex-wrap gap-3 mb-8" data-testid="ops-quick-links">
        {QUICK_LINKS.map((l) => (
          <button key={l.testid} onClick={() => navigate(l.to)} data-testid={l.testid}
            className="inline-flex items-center gap-2 text-xs px-3.5 py-2 rounded-full border border-white/10 text-slate-300 hover:border-cyan-400/40 hover:text-cyan-300 transition-colors">
            <l.icon className="w-3.5 h-3.5" /> {l.label}
          </button>
        ))}
      </div>

      <div className="text-center">
        <button onClick={() => navigate("/operations/advanced")} data-testid="ops-open-full-hub"
          className="text-xs text-slate-500 hover:text-cyan-300 inline-flex items-center gap-1.5 transition-colors">
          Open Full Operations Hub — tasks, approval chains & notifications <ArrowRight className="w-3 h-3" />
        </button>
      </div>
    </div>
  );
}
